const Address = require('@/lib/Addresses/Address');
const { apiResponseToError } = require('@/lib/Api');
const { hydrate } = require('@/lib/Hydration');

class SavedAddressManager {
    /**
     * @param {CartService} cartService
     * @param savedAddressResource
     */
    constructor(
        cartService,
        savedAddressResource
    ) {
        this.cartService = cartService;
        this.savedAddressResource = savedAddressResource;
    }

    /**
     * @private
     *
     * Something about the account has been modified, so let any services that might want an updated
     * copy of the account know.
     *
     * @param {CustomerAccount} account
     */
    updateOtherCopiesOfAccount(account) {
        const cartAccountID = this.cartService.currentCart?.customer?.account?.ID;
        if (cartAccountID === account.ID) {
            // This account is the currently selected account on the cart.
            // Replace it with the fresh copy.
            this.cartService.modifyCurrentCart((cart) => {
                cart.customer.setAccount(account);
            });
        }
    }

    /**
     * Persist a new address against a customer account.
     *
     * @param {Address} address
     * @param {CustomerAccount} account
     *
     * @return {Promise<Address>}
     */
    async addAddressToAccount(address, account) {
        let creationResult;
        try {
            creationResult = await this.savedAddressResource.create(
                Object.assign({}, address.toServerData(), { customerAccountID: account.ID })
            ).$promise;
        } catch (error) {
            throw new Error((error.data && error.data.error) ? error.data.error : error.message);
        }

        const savedAddress = hydrate(creationResult[0], new Address());
        account.addresses.push(savedAddress);
        account.refreshPrimaryAddress();
        this.updateOtherCopiesOfAccount(account);

        return savedAddress;
    }

    async updateAddressOnAccount(address, account) {
        return new Promise((resolve, reject) => {
            this.savedAddressResource.update(
                {
                    savedAddressID: address.savedAddressID,
                },
                address.toServerData(),
                () => {
                    this.updateOtherCopiesOfAccount(account);
                    resolve();
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    }

    /**
     * Remove an address from a customer account
     *
     * @param {Address} address
     * @param {CustomerAccount} account
     * @throws {Error}
     */
    async removeAddressFromAccount(address, account) {
        account.addresses = account.addresses.filter((savedAddress) => (savedAddress.ID !== address.ID));

        try {
            await this.savedAddressResource.delete({
                addressID: address.ID,
                customerAccountID: account.ID,
            }).$promise;
        } catch (error) {
            throw new Error((error.data && error.data.error) ? error.data.error : error.message);
        }

        // If there is only one address remaining in the account the backend will have marked
        // that as the primary address.
        // If there is more than one address remaining the backend will not have automatically
        // chosen a replacement primary address. It will leave the account in a strange state,
        // having no primary address.
        // Let's clean things up and ensure there is a primary address still.
        if (account.addresses.length > 0) {
            // Sort the addresses in order of preference:
            account.sortAddresses();

            // We don't need to do this call if there was one address remaining because the backend
            // will have already marked it as the primary. But it's cleaner to always make this call
            // just to be sure.
            await this.setAddressAsPrimaryOnAccount(account.addresses[0], account);
        }

        this.updateOtherCopiesOfAccount(account);
    }

    async setAddressAsPrimaryOnAccount(address, account) {
        return new Promise((resolve, reject) => {
            this.savedAddressResource.update(
                {
                    savedAddressID: address.savedAddressID,
                },
                {
                    defaultBilling: true,
                },
                () => {
                    address.defaultBilling = true;
                    account.refreshPrimaryAddress();
                    this.updateOtherCopiesOfAccount(account);
                    resolve();
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    }
}

module.exports = SavedAddressManager;
